import { Box, FormControlSizes, SearchSelect, Tooltip } from '@talos/kyoko';
import { useCallback, type ReactNode } from 'react';

interface AggregatedMetricSelectProps {
  /** Active metric id (e.g. `'CapMrktEstUSD'`). Always set, so the control shows it as the value. */
  metric: string;
  /** Selectable metrics — intersection supported by every selected asset. */
  availableMetrics: string[];
  /** Catalog query is loading (or its API key hasn't synced yet). */
  isLoading: boolean;
  /** Catalog query failed — the option list can't be built. */
  isError: boolean;
  /** Whether any assets are selected — distinguishes "select an asset" from "no shared metrics". */
  hasSelectedAssets: boolean;
  onMetricChange: (metric: string) => void;
  testID: string;
  /** Optional leading label (the toolbar passes one; the config view labels via its FormGroup). */
  prefix?: ReactNode;
  /** Fixed width (toolbar). Omit to fill the container (config panel). */
  width?: string;
  /** Maps a metric ID to its display label. Defaults to the raw metric ID. */
  getLabel?: (metricId: string) => string;
  /** Forces the control into a disabled state regardless of loading/error/asset state. */
  disabled?: boolean;
}

/**
 * Metric picker shared by the chart toolbar and the settings panel. The catalog powers the
 * option list (not the chart itself), so a catalog failure only means "can't change metric
 * right now" — we disable the picker and explain why rather than erroring the whole module.
 *
 * `SearchSelect` shows the selected metric (not the placeholder) whenever a value is set, and
 * `metric` is always set — so the disabled reason is surfaced as a tooltip, with the placeholder
 * as a fallback for the (rare) no-value case.
 */
export function AggregatedMetricSelect({
  metric,
  availableMetrics,
  isLoading,
  isError,
  hasSelectedAssets,
  onMetricChange,
  testID,
  prefix,
  width,
  getLabel = metricId => metricId,
  disabled = false,
}: AggregatedMetricSelectProps) {
  // SearchSelect emits `undefined` on its clear affordance; the chart always needs a metric.
  const handleSelect = useCallback(
    (next: string | undefined) => {
      if (next != null) {
        onMetricChange(next);
      }
    },
    [onMetricChange]
  );

  // Resolve a single reason the picker is non-interactive, most-urgent first. `undefined` = enabled.
  let disabledReason: string | undefined;
  if (disabled) {
    disabledReason = '';
  } else if (isError) {
    disabledReason = "Couldn't load metric list";
  } else if (isLoading) {
    disabledReason = 'Loading metric list…';
  } else if (!hasSelectedAssets) {
    disabledReason = 'Select an asset first';
  } else if (availableMetrics.length === 0) {
    disabledReason = 'No metrics available for these assets';
  }

  const select = (
    <SearchSelect
      size={FormControlSizes.Small}
      w="100%"
      selection={metric}
      options={availableMetrics}
      getLabel={getLabel}
      onChange={handleSelect}
      disabled={disabledReason != null}
      placeholder={disabledReason ?? 'Select metric'}
      prefix={prefix}
      portalize
      data-testid={testID}
    />
  );

  const sized = <Box w={width}>{select}</Box>;

  // Tooltip surfaces the reason even when the current metric value is showing (placeholder hidden).
  // Falsy check (not != null) so an empty disabledReason (general disabled, no message) skips the tooltip.
  return disabledReason ? (
    <Tooltip tooltip={disabledReason} usePortal>
      {sized}
    </Tooltip>
  ) : (
    sized
  );
}
